import * as React from "react"
import PhotoCrossfade from "@/components/PhotoCrossfade"
import { filmStyle } from "@/lib/films"
import { cn } from "@/lib/utils"

export interface HeroPhoto {
  id: number
  url: string | null
  alt_text: string | null
  /** The film the section picked to lay over it — see @/lib/films. The
   *  server sends it with the photo, so a component handed the photo is
   *  handed the whole look. */
  film?: string | null
}

/**
 * A chosen photo under the film its section picked — navy, the colour of the
 * band behind it, unless the admin chose otherwise — so white copy reads on
 * any picture. Absolutely positioned: the parent must be `relative` and paint
 * its own copy above it. Shared by the page heroes and the home page's
 * contact band, so the same photo looks the same on both.
 */
export function HeroFilm({ photo }: { photo?: HeroPhoto | null }) {
  if (!photo?.url) return null
  return (
    <div className="absolute inset-0" aria-hidden="true">
      <img
        src={photo.url}
        alt={photo.alt_text ?? ""}
        className="h-full w-full object-cover"
        style={{ objectPosition: "center 35%" }}
        loading="eager"
        decoding="sync"
        fetchPriority="high"
      />
      <div className="absolute inset-0" style={{ background: filmStyle(photo.film) }} />
    </div>
  )
}

/**
 * The same film over several photos instead of one: the section's display
 * settings decide whether they rotate (slideshow) or it is the single chosen
 * photo (static), so this component treats both the same way — one slide means
 * nothing moves. No photos placed means the band keeps its plain blue. The
 * film comes beside the slides rather than on them: it belongs to the
 * section, not to any one picture.
 */
export function HeroFilmSlides({
  slides,
  intervalMs,
  film,
}: {
  slides: { url: string | null; alt_text: string | null }[]
  intervalMs?: number
  film?: string | null
}) {
  const usable = slides.filter((s): s is { url: string; alt_text: string | null } => !!s.url)
  if (usable.length === 0) return null

  return (
    <div className="absolute inset-0" aria-hidden="true">
      <PhotoCrossfade
        slides={usable}
        dotLabel={(n) => String(n)}
        showDots={false}
        intervalMs={intervalMs}
        className="absolute inset-0"
      />
      <div className="absolute inset-0" style={{ background: filmStyle(film) }} />
    </div>
  )
}

/**
 * The page hero on Gisela's site: a short blue band, or, when the admin has
 * chosen a photo in the page's "Hero background" slot, that photo under a blue
 * film the same colour as the band so the white copy reads on any picture.
 * Kept deliberately short — no buttons — so the page's own content is in view
 * straight away; the ways in are in the nav and the closing band.
 */
export function HeroBand({
  photo,
  className,
  children,
}: {
  photo?: HeroPhoto | null
  className?: string
  children: React.ReactNode
}) {
  return (
    <section className="relative overflow-hidden bg-teal pt-28 pb-12 text-white">
      <HeroFilm photo={photo} />
      <div className={cn("relative mx-auto max-w-6xl px-6 lg:px-8", className)}>{children}</div>
    </section>
  )
}
